import { Box, Button, Group, NumberInput, Stack, Table, Text, Title } from '@mantine/core';
import type { QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';

const ESS_SCENARIOS = [
  { linkId: 'ess-reading', label: 'Sitting and reading' },
  { linkId: 'ess-television', label: 'Watching TV' },
  { linkId: 'ess-inactive', label: 'Sitting inactive in a public space (e.g. a meeting, theatre, or dinner)' },
  { linkId: 'ess-passenger', label: 'As a passenger in a car for an hour without stopping' },
  { linkId: 'ess-lying-down', label: 'Lying down to rest, when circumstances permit' },
  { linkId: 'ess-talking', label: 'Sitting down talking to someone' },
  { linkId: 'ess-after-meal', label: 'Sitting quietly after a meal without alcohol' },
  { linkId: 'ess-traffic', label: 'In a car, while stopped for a few minutes (e.g. at a light)' },
] as const;

const ESS_COLUMNS = [
  { code: '0', label: 'Would never nod off' },
  { code: '1', label: 'Low chance' },
  { code: '2', label: 'Moderate chance' },
  { code: '3', label: 'High chance' },
] as const;

const STOP_BANG_YES_NO = [
  { linkId: 'snore-loudly', label: 'Do you snore loudly (loud enough to be heard through a closed door)?' },
  { linkId: 'tired-fatigued', label: 'Do you often feel tired, fatigued, or sleepy during the daytime?' },
  { linkId: 'stop-breathing', label: 'Has anyone observed you stop breathing during sleep?' },
  { linkId: 'high-blood-pressure', label: 'Do you have, or are you being treated for, high blood pressure?' },
  { linkId: 'age-50-or-older', label: 'Are you 50 years old or older?' },
] as const;

const STOP_BANG_ALL_QUESTIONS = [
  { linkId: 'snore-loudly', text: 'Do you snore loudly (loud enough to be heard through a closed door)?' },
  { linkId: 'tired-fatigued', text: 'Do you often feel tired, fatigued, or sleepy during the daytime?' },
  { linkId: 'stop-breathing', text: 'Has anyone observed you stop breathing during sleep?' },
  { linkId: 'high-blood-pressure', text: 'Do you have, or are you being treated for, high blood pressure?' },
  { linkId: 'age-50-or-older', text: 'Are you 50 years old or older?' },
  { linkId: 'height-ft', text: 'Height (feet)' },
  { linkId: 'height-in', text: 'Height (inches)' },
  { linkId: 'weight', text: 'Weight (pounds)' },
  { linkId: 'neck-circumference-over-16', text: 'Is your neck circumference greater than 16 inches?' },
  { linkId: 'male-or-assigned-male', text: 'Are you male, or assigned male at birth?' },
] as const;

export interface StopBangAnswers {
  'snore-loudly'?: string;
  'tired-fatigued'?: string;
  'stop-breathing'?: string;
  'high-blood-pressure'?: string;
  heightFt?: number;
  heightIn?: number;
  weightLbs?: number;
  'age-50-or-older'?: string;
  'neck-circumference-over-16'?: string;
  'male-or-assigned-male'?: string;
}

export function buildEssQR(answers: Record<string, string>): QuestionnaireResponse {
  return {
    resourceType: 'QuestionnaireResponse',
    status: 'completed',
    item: ESS_SCENARIOS.map(({ linkId, label }) => ({
      linkId,
      text: label,
      answer: [{ valueCoding: { code: answers[linkId] } }],
    })),
  };
}

export function buildStopBangQR(answers: StopBangAnswers): QuestionnaireResponse {
  const item: QuestionnaireResponseItem[] = [];
  for (const { linkId, text } of STOP_BANG_ALL_QUESTIONS) {
    switch (linkId) {
      case 'height-ft':
        item.push({ linkId, text, answer: [{ valueInteger: answers.heightFt }] });
        break;
      case 'height-in':
        item.push({ linkId, text, answer: [{ valueInteger: answers.heightIn }] });
        break;
      case 'weight':
        item.push({ linkId, text, answer: [{ valueDecimal: answers.weightLbs }] });
        break;
      case 'neck-circumference-over-16':
        item.push({ linkId, text, answer: [{ valueString: answers[linkId] as string }] });
        break;
      case 'male-or-assigned-male':
        item.push({ linkId, text, answer: [{ valueString: answers[linkId] as string }] });
        break;
      default:
        item.push({ linkId, text, answer: [{ valueString: answers[linkId] as string }] });
    }
  }
  return { resourceType: 'QuestionnaireResponse', status: 'completed', item };
}

interface SleepHistoryContentProps {
  submitting: boolean;
  onSubmit: (ess: QuestionnaireResponse, stopBang: QuestionnaireResponse) => void;
}

export default function SleepHistoryContent({ submitting, onSubmit }: SleepHistoryContentProps): JSX.Element {
  const [essAnswers, setEssAnswers] = useState<Record<string, string>>({});
  const [stopBangAnswers, setStopBangAnswers] = useState<StopBangAnswers>({});

  const essComplete = ESS_SCENARIOS.every(({ linkId }) => essAnswers[linkId] !== undefined);
  const stopBangComplete =
    STOP_BANG_YES_NO.every(({ linkId }) => stopBangAnswers[linkId] !== undefined) &&
    stopBangAnswers.heightFt !== undefined &&
    stopBangAnswers.heightIn !== undefined &&
    stopBangAnswers.weightLbs !== undefined &&
    stopBangAnswers['neck-circumference-over-16'] !== undefined &&
    stopBangAnswers['male-or-assigned-male'] !== undefined;

  function handleSubmit(): void {
    if (!essComplete || !stopBangComplete) {
      return;
    }
    onSubmit(buildEssQR(essAnswers), buildStopBangQR(stopBangAnswers));
  }

  function setStopBang(key: keyof StopBangAnswers, value: string): void {
    setStopBangAnswers((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <Stack gap="xl">
      {/* Part one: Epworth Sleepiness Scale */}
      <Box>
        <Text fw={600} size="sm" mb={2}>
          Part one · Epworth Sleepiness Scale
        </Text>
        <Text size="sm" c="dimmed" mb="md">
          In each scenario, indicate how likely you are to nod off (fall briefly asleep).
        </Text>
        <Box style={{ overflowX: 'auto' }}>
          <Table withTableBorder withColumnBorders>
            <Table.Thead bg="gray.0">
              <Table.Tr>
                <Table.Th style={{ minWidth: 200 }}>Scenario</Table.Th>
                {ESS_COLUMNS.map((col) => (
                  <Table.Th key={col.code} ta="center" style={{ minWidth: 100 }}>
                    <Text size="xs" fw={500}>
                      {col.label}
                    </Text>
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {ESS_SCENARIOS.map(({ linkId, label }) => (
                <Table.Tr key={linkId} bg={essAnswers[linkId] !== undefined ? 'polaris.0' : undefined}>
                  <Table.Td>
                    <Text size="sm">{label}</Text>
                  </Table.Td>
                  {ESS_COLUMNS.map((col) => (
                    <Table.Td key={col.code} ta="center">
                      <input
                        type="radio"
                        name={linkId}
                        value={col.code}
                        checked={essAnswers[linkId] === col.code}
                        onChange={() => setEssAnswers((prev) => ({ ...prev, [linkId]: col.code }))}
                        aria-label={`${label}: ${col.label}`}
                      />
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      </Box>

      {/* Part two: STOP-BANG */}
      <Box>
        <Text fw={600} size="sm" mb={2}>
          Part two · STOP-BANG questionnaire
        </Text>
        <Text size="sm" c="dimmed" mb="md">
          Answer yes or no for each question.
        </Text>
        <Stack gap={0}>
          {STOP_BANG_YES_NO.map(({ linkId, label }) => (
            <Box key={linkId} py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
              <Group justify="space-between" wrap="nowrap" gap="xl">
                <Text size="sm">{label}</Text>
                <Button.Group>
                  {(['Yes', 'No'] as const).map((opt) => (
                    <Button
                      key={opt}
                      variant={stopBangAnswers[linkId] === opt ? 'filled' : 'outline'}
                      color={stopBangAnswers[linkId] === opt ? 'polaris' : 'gray'}
                      size="sm"
                      onClick={() => setStopBang(linkId, opt)}
                    >
                      {opt}
                    </Button>
                  ))}
                </Button.Group>
              </Group>
            </Box>
          ))}

          {/* Height and weight */}
          <Box py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
            <Group justify="space-between" wrap="nowrap" gap="xl">
              <Text size="sm">Your height and weight</Text>
              <Group gap="xs">
                <NumberInput
                  value={stopBangAnswers.heightFt ?? ''}
                  onChange={(val) =>
                    setStopBangAnswers((prev) => ({ ...prev, heightFt: typeof val === 'number' ? val : undefined }))
                  }
                  min={3}
                  max={8}
                  w={70}
                  placeholder="5"
                  hideControls
                  aria-label="Height feet"
                />
                <Text size="sm" fw={500}>
                  ft
                </Text>
                <NumberInput
                  value={stopBangAnswers.heightIn ?? ''}
                  onChange={(val) =>
                    setStopBangAnswers((prev) => ({ ...prev, heightIn: typeof val === 'number' ? val : undefined }))
                  }
                  min={0}
                  max={11}
                  w={70}
                  placeholder="7"
                  hideControls
                  aria-label="Height inches"
                />
                <Text size="sm" fw={500}>
                  in
                </Text>
                <NumberInput
                  value={stopBangAnswers.weightLbs ?? ''}
                  onChange={(val) =>
                    setStopBangAnswers((prev) => ({ ...prev, weightLbs: typeof val === 'number' ? val : undefined }))
                  }
                  min={50}
                  max={700}
                  w={80}
                  placeholder="180"
                  hideControls
                  aria-label="Weight pounds"
                />
                <Text size="sm" fw={500}>
                  lb
                </Text>
              </Group>
            </Group>
          </Box>

          {/* Neck circumference: 3 options */}
          <Box py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
            <Group justify="space-between" wrap="nowrap" gap="xl">
              <Text size="sm">Is your neck circumference greater than 16 inches?</Text>
              <Button.Group>
                {(['Yes', 'No', "I don't know"] as const).map((opt) => (
                  <Button
                    key={opt}
                    variant={stopBangAnswers['neck-circumference-over-16'] === opt ? 'filled' : 'outline'}
                    color={stopBangAnswers['neck-circumference-over-16'] === opt ? 'polaris' : 'gray'}
                    size="sm"
                    onClick={() => setStopBang('neck-circumference-over-16', opt)}
                  >
                    {opt}
                  </Button>
                ))}
              </Button.Group>
            </Group>
          </Box>

          {/* Male or assigned male */}
          <Box py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
            <Group justify="space-between" wrap="nowrap" gap="xl">
              <Text size="sm">Are you male, or assigned male at birth?</Text>
              <Button.Group>
                {(['Yes', 'No'] as const).map((opt) => (
                  <Button
                    key={opt}
                    variant={stopBangAnswers['male-or-assigned-male'] === opt ? 'filled' : 'outline'}
                    color={stopBangAnswers['male-or-assigned-male'] === opt ? 'polaris' : 'gray'}
                    size="sm"
                    onClick={() => setStopBang('male-or-assigned-male', opt)}
                  >
                    {opt}
                  </Button>
                ))}
              </Button.Group>
            </Group>
          </Box>
        </Stack>
      </Box>

      <Group justify="flex-end" mt="md">
        <Button onClick={handleSubmit} disabled={!essComplete || !stopBangComplete || submitting} loading={submitting}>
          Submit
        </Button>
      </Group>
    </Stack>
  );
}
